import React from "react";
import { StyleSheet, View, ScrollView, Platform, type StyleProp, type ViewStyle } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { ThemedText } from "../../ThemedText";
import { ThemedButton } from "../../ThemedButton";
import { ThemedToolTip } from "../../ThemedToolTip";
import { useThemeColor } from "@/hooks/useThemeColor";

export interface IInsertToolbarProps {
  visible?: boolean;
  onInsertContact?: () => void;
  onInsertGroup?: () => void;
  onInsertMedia?: () => void;
  onInsertMemory?: () => void;
  onInsertTag?: () => void;
  onInsertLink?: () => void;
  onInsertTable?: () => void;
  isStandalonePreview?: boolean;
  style?: StyleProp<ViewStyle>;
}

/**
 * InsertToolbar
 *
 * Dedicated toolbar alongside EditToolbar for inserting interactive entities,
 * media, links, tables, and tags into the content stream at the current cursor position.
 */
export const InsertToolbar: React.FC<IInsertToolbarProps> = ({
  visible = true,
  onInsertContact,
  onInsertGroup,
  onInsertMedia,
  onInsertMemory,
  onInsertTag,
  onInsertLink,
  onInsertTable,
  isStandalonePreview = false,
  style,
}) => {
  const cardBg = useThemeColor({}, "card");
  const borderColor = useThemeColor({}, "border");
  const textColor = useThemeColor({}, "text");
  const tintColor = useThemeColor({}, "tint");
  const isDark = cardBg === "#131520";

  if (!visible) return null;

  const handleMouseDown = (e: any) => {
    if (Platform.OS === "web") {
      e.preventDefault();
    }
  };

  return (
    <View
      style={[
        styles.container,
        {
          backgroundColor: cardBg,
          borderColor,
          boxShadow: isDark
            ? "0 4px 16px rgba(0,0,0,0.5)"
            : "0 4px 16px rgba(0,0,0,0.1)",
        } as any,
        isStandalonePreview && styles.standaloneContainer,
        style,
      ]}
      onMouseDown={handleMouseDown}
    >
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.scrollContent}
      >
        <ThemedText style={styles.sectionLabel}>Insert:</ThemedText>

        {/* Contact (@) */}
        <ThemedToolTip tooltip="Insert Contact (@)">
          <ThemedButton
            title="Contact"
            variant="ghost"
            icon={<Ionicons name="person-outline" size={15} color={tintColor} />}
            onPress={onInsertContact}
            style={styles.actionBtn}
          />
        </ThemedToolTip>

        {/* Group (#) */}
        <ThemedToolTip tooltip="Insert Group (#)">
          <ThemedButton
            title="Group"
            variant="ghost"
            icon={<Ionicons name="people-outline" size={15} color={tintColor} />}
            onPress={onInsertGroup}
            style={styles.actionBtn}
          />
        </ThemedToolTip>

        {/* Media */}
        <ThemedToolTip tooltip="Insert Media / Image">
          <ThemedButton
            title="Media"
            variant="ghost"
            icon={<Ionicons name="images-outline" size={15} color={tintColor} />}
            onPress={onInsertMedia}
            style={styles.actionBtn}
          />
        </ThemedToolTip>

        {/* Memory (|) */}
        <ThemedToolTip tooltip="Insert Memory (|)">
          <ThemedButton
            title="Memory"
            variant="ghost"
            icon={<Ionicons name="book-outline" size={15} color={tintColor} />}
            onPress={onInsertMemory}
            style={styles.actionBtn}
          />
        </ThemedToolTip>

        {/* Tag (^) */}
        <ThemedToolTip tooltip="Insert Tag (^)">
          <ThemedButton
            title="Tag"
            variant="ghost"
            icon={<Ionicons name="pricetag-outline" size={15} color={tintColor} />}
            onPress={onInsertTag}
            style={styles.actionBtn}
          />
        </ThemedToolTip>

        {/* Web Link (!) */}
        <ThemedToolTip tooltip="Insert Web Link (!)">
          <ThemedButton
            title="Link"
            variant="ghost"
            icon={<Ionicons name="link-outline" size={15} color={tintColor} />}
            onPress={onInsertLink}
            style={styles.actionBtn}
          />
        </ThemedToolTip>

        {/* Table */}
        <ThemedToolTip tooltip="Insert Responsive Table">
          <ThemedButton
            title="Table"
            variant="ghost"
            icon={<Ionicons name="grid-outline" size={15} color={tintColor} />}
            onPress={onInsertTable}
            style={styles.actionBtn}
          />
        </ThemedToolTip>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 8,
    paddingVertical: 4,
    minHeight: 38,
  },
  standaloneContainer: {
    position: "relative",
    top: 0,
    left: 0,
  },
  scrollContent: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  sectionLabel: {
    fontSize: 11,
    fontWeight: "700",
    opacity: 0.6,
    marginRight: 4,
  },
  actionBtn: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    minHeight: 28,
  },
});
